import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { QUOTA_KEYS, planName, quotaLabel } from "@/components/billing/PlanCards";
import api, { errMsg } from "@/lib/api";
import { t, locale } from "@/i18n";

const fmtDate = (d) => new Date(d).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

export function SubscriptionCard() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: `${window.location.origin}/settings` });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  useEffect(() => { api.get("/billing").then((r) => setB(r.data)).catch(() => {}); }, []);
  if (!b) return null;
  return (
    <div className="max-w-2xl space-y-5 rounded-2xl border bg-card p-6" data-testid="subscription-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("sub.title")}</p>
          <p className="mt-1 text-xl font-semibold" data-testid="subscription-plan">{planName(b.plan)}
            {b.interval && <span className="ml-2 text-sm font-normal text-muted-foreground" data-testid="subscription-interval">· {t(b.interval === "year" ? "plans.yearly" : "plans.monthly")}</span>}</p>
          {b.plan_expires_at && (
            <p className="mt-1 text-sm text-primary" data-testid="subscription-expiry">
              {t(b.trial ? "sub.trial" : b.cancel_at_period_end ? "sub.cancelled" : "sub.ends", { date: fmtDate(b.plan_expires_at) })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {b.can_manage && (
            <Button onClick={manage} disabled={busy} data-testid="subscription-manage-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("sub.manage")}
            </Button>
          )}
          <Button asChild variant="outline" data-testid="subscription-change-btn"><Link to="/tarifs">{t("sub.change")}</Link></Button>
        </div>
      </div>
      {b.can_manage && <p className="text-xs text-muted-foreground" data-testid="subscription-manage-hint">{t("sub.manage_hint")}</p>}
      <div className="space-y-3">
        <p className="text-sm font-medium">{t("sub.usage")}</p>
        {QUOTA_KEYS.map((k) => {
          const used = b.usage[k] ?? 0, lim = b.limits[k];
          return (
            <div key={k} data-testid={`usage-${k}`}>
              <div className="flex justify-between text-xs text-muted-foreground"><span>{quotaLabel(k)}</span><span className="num">{used} / {lim ?? "∞"}</span></div>
              <Progress value={lim ? Math.min(100, (used / lim) * 100) : 0} className="mt-1 h-1.5" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
